import { getCookie } from 'cookies-next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import useDialog from 'hooks/useDialog';
import usePendingComments from 'hooks/usePendingComments';
import useSingleton from 'hooks/useSingleton';
import useToast from 'hooks/useToast';

import {
  postGiveLGPDConsents,
  resetLGPDConsentsStorage,
  sendUserToAnalytics,
} from 'lib/lgpd';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import placeholder from 'lib/placeholder';
import { initPostHog } from 'lib/posthog/client';
import { sendSupixEvent } from 'lib/supix';
import { clearLoginStorage, getLoggedInUser } from 'lib/user';
import { browserOnly, isDenied, isDev, noop } from 'lib/utils';

import { useLocalWishlist } from './LocalWishlistProvider';

import COOKIES from 'constants/cookies';
import LOCAL_STORAGE from 'constants/localStorage';
import LOGIN from 'constants/login';

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const BROADCAST_CHANNEL_FALLBACK = {
  addEventListener: noop,
  removeEventListener: noop,
  postMessage: noop,
};
const LOGIN_REALIZED_EVENT = 'login';
const LOGOUT_REALIZED_EVENT = 'logout';
const AUTH_EXPIRATION_TIME = 60 * 60 * 24 * 365; // 1 year
const TIMEOUT_TO_CREATE_RECOVERED_COMMENTS = 3000; // 3 seconds

const UserContext = createContext({
  LoggedInUser: null,
  isLoggedIn: false,
  loadingLoggedInUser: true,
  login: noop,
  logout: noop,
  runCallbackIfLoggedIn: noop,
});

const clearPreferences = async () => {
  await clearLoginStorage();
  removeFromLocalStorage(LOCAL_STORAGE.IS_DARK_MODE_ACTIVE);
  resetLGPDConsentsStorage();
};

/**
 * These functions are used to handle the retroactive for a migration of domains
 * of Supix to reduce problematic thirty party cookies at chromium-based
 * browsers.
 */
const hasOldSupixCookie = () => {
  const supixPersistedSession = getCookie('sh');
  const supixSession = getFromLocalStorage(
    LOCAL_STORAGE.SUPIX_GENERATED_SESSION
  );

  // We consider persisted cookie as the hottest session
  return supixPersistedSession !== supixSession;
};

const requestSubscription = async () => {
  const isNotificationsSubscriptionOnboardingHappened = !!getFromLocalStorage(
    LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION
  );

  if (
    isNotificationsSubscriptionOnboardingHappened ||
    !('Notification' in window) ||
    isDenied(Notification.permission)
  ) {
    return;
  }

  const { requestNotificationSubscription, isNotificationsSupported } =
    await import('lib/notificationsSubscription');

  const isSupported = await isNotificationsSupported();

  if (!isSupported) {
    return;
  }

  await requestNotificationSubscription();
};

const UserProvider = ({ children, ssrAuthToken }) => {
  const { showDialog } = useDialog();
  const { showToast } = useToast();
  const router = useRouter();
  const {
    localOfferKeywords,
    localCouponKeywords,
    sendLocalCouponKeywordsToServer,
    sendLocalOfferKeywordsToServer,
  } = useLocalWishlist();
  const { sendPendingComments } = usePendingComments();
  const [loadingLoggedInUser, setLoadingLoggedInUser] = useState(true);
  const [user, setUser] = useState(null);
  /**
   * Despite the majority of browsers already support BroadcastChannel API, we
   * have a lot of users that uses mobile safari <15.4 and because of that, to
   * avoid errors caused by a secondary feature, in these edge-cases we store
   * in a dummy object.
   */
  const channel = useSingleton(
    browserOnly(() =>
      'BroadcastChannel' in window
        ? new BroadcastChannel('login_cannel')
        : BROADCAST_CHANNEL_FALLBACK
    )
  );

  const login = useCallback(
    async ({ token }) => {
      try {
        const [
          { setCookie },
          { postCreateWebpush },
          { removeFromSessionStorage },
        ] = await Promise.all([
          import('cookies-next'),
          import('services/webpush'),
          import('lib/sessionStorage'),
        ]);

        setLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN, token);
        removeFromLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION);
        setCookie(COOKIES.IS_USER_LOGGED, true, {
          maxAge: AUTH_EXPIRATION_TIME,
          sameSite: true,
        });
        removeFromSessionStorage('utm_campaign');

        if (localOfferKeywords.length > 0) {
          await sendLocalOfferKeywordsToServer();
        }

        const hadPendingCoupons = localCouponKeywords.length > 0;
        const couponsWereSynced =
          hadPendingCoupons && (await sendLocalCouponKeywordsToServer());

        if (couponsWereSynced) {
          setLocalStorage(LOCAL_STORAGE.POST_LOGIN_TOAST, 'wishlist_synced');
        }

        const webpushHash = getFromLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH);

        if (webpushHash) {
          await postCreateWebpush({ userAuthToken: token, webpushHash });
        }

        await postGiveLGPDConsents();
        channel.postMessage(LOGIN_REALIZED_EVENT);

        window.location.reload();
      } catch (e) {
        const { captureException } = await import('@sentry/nextjs');

        await clearPreferences();
        captureException(e);
        setLoadingLoggedInUser(false);
      }
    },
    [localOfferKeywords, localCouponKeywords]
  );

  const logout = useCallback(async () => {
    // Reset PostHog identity — generates new anonymous ID
    initPostHog().then((ph) => ph?.reset());

    await sendSupixEvent('/clr');
    await clearPreferences();

    channel.postMessage(LOGOUT_REALIZED_EVENT);

    // Workaround for legacy compat logout
    if (isDev()) {
      window.location.reload();
      return;
    }

    window.location.assign(
      `https://${process.env.NEXT_PUBLIC_SUBDOMAIN}.promobit.com.br/User/logout`
    );
  }, []);

  const runCallbackIfLoggedIn = useCallback(
    (callback, ...args) => {
      if (loadingLoggedInUser) {
        return;
      }

      if (!user) {
        showDialog(LoginDialog);
        return;
      }

      callback(...args);
    },
    [user, loadingLoggedInUser]
  );

  useEffect(() => {
    const checkLoginValidity = async () => {
      const token =
        getFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN) || ssrAuthToken;

      if (!token) {
        // Mark anonymous user for PostHog experiment targeting
        initPostHog().then((ph) =>
          ph?.setPersonProperties({ is_logged_in: false })
        );
        setLoadingLoggedInUser(false);
        setUser(null);
        return;
      }

      const loggedInUser = await getLoggedInUser({
        skipOldApiWorkaround: process.env.NODE_ENV === 'development',
        token,
      });

      if (!loggedInUser) {
        await clearPreferences();
        initPostHog().then((ph) =>
          ph?.setPersonProperties({ is_logged_in: false })
        );
        setLoadingLoggedInUser(false);
        setUser(null);
        return;
      }

      const [
        { saveUserAsLegacyModeToLocalStorage },
        { setUser: setSentryUser },
      ] = await Promise.all([import('lib/user'), import('@sentry/nextjs')]);

      const hasOldSupixVersion = hasOldSupixCookie();

      if (hasOldSupixVersion) {
        removeFromLocalStorage(LOCAL_STORAGE.SUPIX_GENERATED_SESSION);
      }

      if (
        !getFromLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED) ||
        hasOldSupixVersion
      ) {
        await sendSupixEvent('/usa', { id: loggedInUser.userId });
      }

      // Workaround for legacy compat mode
      saveUserAsLegacyModeToLocalStorage(loggedInUser);
      sendUserToAnalytics(loggedInUser);
      setLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED, 'true');
      setUser(loggedInUser);
      setLoadingLoggedInUser(false);
      setSentryUser({
        id: loggedInUser.userId,
        username: loggedInUser.userUsername,
      });

      // Identify user in PostHog for experiment targeting.
      // Also triggers a flag reload with new person properties.
      // Only send is_logged_in — no PII (username) to third-party.
      // Cross-reference userId in our DB if needed.
      initPostHog().then((ph) =>
        ph?.identify(String(loggedInUser.userId), {
          is_logged_in: true,
        })
      );
    };

    checkLoginValidity();
  }, []);

  useEffect(() => {
    const onMessage = async (event) => {
      if (
        event.data === LOGIN_REALIZED_EVENT ||
        event.data === LOGOUT_REALIZED_EVENT
      ) {
        window.location.reload();
      }
    };

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    if (loadingLoggedInUser) {
      return;
    }

    const handleRedirectAfterLogin = () => {
      if (!user) {
        return;
      }

      const redirectPathname = router.query[LOGIN.AFTER_LOGIN_REDIRECT_QS];

      if (!redirectPathname) {
        return;
      }

      router.replace(redirectPathname);
    };

    const handlePendingComments = () => {
      if (!user) {
        return;
      }

      /**
       * In some unknown case where the user got logged in and we try to send
       * the comments too fast right after that the server returns a 500 error.
       * To mitigate this on our side, we wait a generous
       * `TIMEOUT_TO_CREATE_RECOVERED_COMMENTS` timeout to happen before sending
       * them.
       */
      setTimeout(sendPendingComments, TIMEOUT_TO_CREATE_RECOVERED_COMMENTS);
    };

    handleRedirectAfterLogin();
    requestSubscription();
    handlePendingComments();
  }, [user]);

  useEffect(() => {
    const toastType = getFromLocalStorage(LOCAL_STORAGE.POST_LOGIN_TOAST);

    if (!toastType) {
      return;
    }

    removeFromLocalStorage(LOCAL_STORAGE.POST_LOGIN_TOAST);

    if (toastType === 'wishlist_synced') {
      showToast({
        type: 'success',
        text: placeholder('toasts.successes.wishlist.keywordAdded'),
      });
    }
  }, []);

  const value = useMemo(
    () => ({
      LoggedInUser: user,
      isLoggedIn: !!user,
      loadingLoggedInUser,
      setLoggedInUser: setUser,
      login,
      logout,
      runCallbackIfLoggedIn,
    }),
    [user, loadingLoggedInUser, login, logout, runCallbackIfLoggedIn]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserProvider;
